import Image from 'next/image';

import { Box, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { FlashSalePillEyebrow } from '~/features/promotions/components/flash-sale-pill-eyebrow';
import {
  FLASH_SALE_CTA_LABEL,
  FLASH_SALE_HEADLINE,
  FLASH_SALE_RAYS_BG,
  FLASH_SALE_SUBTITLE,
  getFlashSaleCheckoutHref,
} from '~/features/promotions/constants';
import type { FlashTier } from '~/features/promotions/utils/get-flash-tier';

import { RocketIcon } from '../icons';

import { PromoCard } from './promo-card';

/**
 * Homepage June flash-sale card. A thin wrapper around the shared `PromoCard` (like
 * `MbaAiPromoCard`/`MastersAIPromoCard`) so it inherits the card shell and the bottom-pinned
 * (`mt='auto'`) CTA. The eyebrow is the shared catalog `FlashSalePillEyebrow` (bordered cyan pill +
 * translucent divider) so all three flash surfaces stay consistent. Tier picks the headline copy.
 */
export function FlashSalePromoCard({
  tier,
  promoCode,
  imagePriority,
  onClick,
}: {
  tier: FlashTier;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  imagePriority?: boolean;
  onClick?: () => void;
}) {
  const checkoutHref = getFlashSaleCheckoutHref(tier, promoCode);
  return (
    <PromoCard
      backgroundColor='#000b26'
      eyebrowSlot={<FlashSalePillEyebrow />}
      id='flash-sale-title'
      backgroundImageSlot={
        <Box bottom={0} left={0} position='absolute' right={0} top={0}>
          <Image
            alt=''
            loading='eager'
            priority={imagePriority}
            sizes='(max-width: 768px) 100vw, 768px'
            src={FLASH_SALE_RAYS_BG}
            style={{ objectFit: 'cover', objectPosition: 'right center' }}
            fill
          />
          <Box
            bgGradient='linear(245deg, rgba(0, 41, 140, 0) 25%, rgba(0, 11, 38, 0.95) 77%)'
            bottom={0}
            left={0}
            position='absolute'
            right={0}
            top={0}
          />
        </Box>
      }
      buttonSlot={
        <ButtonLink
          linkProps={{ href: checkoutHref }}
          buttonProps={{
            size: 'icon-left',
            leftIcon: <RocketIcon height='18px' width='18px' />,
            variant: 'solid',
            colorScheme: 'blue',
            position: 'relative',
            zIndex: 1,
            width: {
              base: '100%',
              md: '152px',
            },
            onClick,
          }}
        >
          {FLASH_SALE_CTA_LABEL}
        </ButtonLink>
      }
      contentSlot={
        <Box>
          <Heading
            as='h2'
            color='white'
            fontWeight='600'
            id='flash-sale-title'
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.14',
              md: '1.1',
            }}
          >
            <LinkOverlay href={checkoutHref} onClick={onClick}>
              {FLASH_SALE_HEADLINE[tier]}
            </LinkOverlay>
          </Heading>
          <Text color='cyan.300'>{FLASH_SALE_SUBTITLE}</Text>
        </Box>
      }
      // No right-side art to clear (unlike the MBA/Masters cards' rocket/seal), so the right padding
      // matches the left (48px) — letting the content column + pill eyebrow divider reach the standard
      // right padding edge instead of stopping ~262px short.
      padding={{
        base: '32px',
        md: '48px 48px 56px 48px',
      }}
    />
  );
}
